import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import type {
  OrderDetailResponse,
  OrderSummary,
  OrderSendResponse,
  OrderItemsResponse,
  ItemsResponse,
  OrderLineStation,
  OrderLineRequest,
} from '@shared/api-responses';
import { ORDER_STATUS_LABELS, PROGRAM_LABELS, type Item, type OrderItem, type OrderStatus } from '@shared/types';
import { apiFetch } from '../hooks/useApi';
import NumericInput from '../components/NumericInput';
import ProgramBadge from '../components/ProgramBadge';
import { suggestionsFor } from '../lib/orderPicker';
import { orderBreakdown, formatSubmitted } from '../lib/orderBreakdown';

/** Lines stay editable until the order is closed out. */
const EDITABLE: OrderStatus[] = ['draft', 'submitted', 'approved', 'placed'];

/** Resolve typed text to a catalog item (exact name, case/space-insensitive). */
function matchCatalogItem(catalog: Item[], typed: string): Item | undefined {
  const key = typed.trim().toLowerCase();
  return catalog.find((i) => i.name.toLowerCase() === key);
}

/**
 * One editable line. Quantity and note are held locally while the person is
 * typing and pushed on blur — a request per keystroke would fight the numeric
 * keypad on a phone in a store aisle.
 */
function OrderLineEditor({
  line,
  stations,
  busy,
  onSave,
  onRemove,
}: {
  line: OrderItem;
  /** This line's requested split. Empty for a line added during fulfillment. */
  stations: OrderLineStation[];
  busy: boolean;
  onSave: (lineId: number, qty: number, note: string | null) => void;
  onRemove: (lineId: number) => void;
}) {
  const requested = stations.reduce((sum, s) => sum + s.qty, 0);
  // The allocation is never auto-adjusted, so a line edited down to what the
  // store actually had won't sum to its qty. Say so rather than hide it.
  const short = requested > 0 && requested !== line.qty;
  const [qty, setQty] = useState<number | null>(line.qty);
  const [note, setNote] = useState(line.note ?? '');

  // Re-sync when the server sends back a different value (e.g. after a retry).
  useEffect(() => {
    setQty(line.qty);
    setNote(line.note ?? '');
  }, [line.qty, line.note]);

  const dirty = qty !== line.qty || note !== (line.note ?? '');

  function commit() {
    if (!dirty) return;
    if (qty === null || qty < 1) {
      setQty(line.qty); // blank/zero isn't a removal — that's the × button
      return;
    }
    onSave(line.id, qty, note.trim() || null);
  }

  return (
    <div className="py-2 border-b border-border-subtle/50 last:border-0">
      <div className="flex items-center gap-2">
        <span className="flex-1 text-sm text-white">
          {line.item_name}
          {line.source_request_ids === null && <span className="ml-2 text-xs text-zinc-500">added</span>}
        </span>
        <NumericInput
          value={qty}
          onChange={setQty}
          min={1}
          disabled={busy}
          aria-label={`Quantity for ${line.item_name}`}
        />
        <span className="w-12 text-xs text-zinc-400">{line.unit ?? ''}</span>
        <button
          type="button"
          onClick={() => onRemove(line.id)}
          disabled={busy}
          aria-label={`Remove ${line.item_name}`}
          className="min-h-[44px] min-w-[44px] rounded-lg text-zinc-400 hover:text-ems-red disabled:opacity-40"
        >
          ✕
        </button>
      </div>
      {stations.length > 0 && (
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
          {stations.map((s) => (
            <span key={s.station_id} className="text-zinc-400">
              {s.station_name} <span className="font-mono text-zinc-300">{s.qty}</span>
            </span>
          ))}
          {short && <span className="text-ems-amber">{requested} requested</span>}
        </div>
      )}
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={commit}
        placeholder="Note (optional) — e.g. store was out"
        disabled={busy}
        className="mt-1 w-full rounded-lg bg-surface-raised/60 border border-border-subtle px-2 py-1 text-xs text-zinc-300"
      />
      {dirty && (
        <button type="button" onClick={commit} disabled={busy} className="mt-1 text-xs text-dcvfd-accent underline">
          Save change
        </button>
      )}
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const orderId = Number(id);
  const [order, setOrder] = useState<OrderSummary | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [emailHistory, setEmailHistory] = useState<string[]>([]);
  const [recipient, setRecipient] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [newItem, setNewItem] = useState<{ name: string; qty: number | null; unit: string; note: string }>({
    name: '',
    qty: 1,
    unit: '',
    note: '',
  });
  const [catalog, setCatalog] = useState<Item[]>([]);
  const [stationSplit, setStationSplit] = useState<OrderLineStation[]>([]);
  const [requestSplit, setRequestSplit] = useState<OrderLineRequest[]>([]);

  // The catalog backs the add-item picker, so a line added mid-fulfillment keeps
  // its item_id and still joins back to `items`.
  //
  // Scoped to this order's program: the worker rejects a cross-program add, so
  // offering the other program's items would only ever produce an error.
  const orderProgram = order?.program;
  useEffect(() => {
    if (!orderProgram) return;
    apiFetch<ItemsResponse>(`/items?program=${orderProgram}`)
      .then((r) => setCatalog(r.items.filter((i) => i.is_active)))
      .catch(() => setCatalog([])); // picker degrades to free text
  }, [orderProgram]);

  const load = useCallback(() => {
    apiFetch<OrderDetailResponse>(`/orders/${orderId}`).then((d) => {
      setOrder(d.order);
      setItems(d.items);
      setStationSplit(d.stationSplit);
      setRequestSplit(d.requestSplit);
      setEmailHistory(d.emailHistory);
      if (!recipient) setRecipient(d.emailHistory[0] ?? '');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => load(), [load]);

  async function setStatus(status: OrderStatus) {
    setBusy(true);
    setMsg(null);
    try {
      await apiFetch(`/orders/${orderId}/status`, { method: 'PUT', body: { status } });
      load();
    } catch {
      setMsg('Could not update status.');
    } finally {
      setBusy(false);
    }
  }

  /** Persist a line change (qty and/or note). Server returns the live lines. */
  async function saveLine(lineId: number, qty: number, note: string | null) {
    setBusy(true);
    setMsg(null);
    try {
      const res = await apiFetch<OrderItemsResponse>(`/orders/${orderId}/items/${lineId}`, {
        method: 'PUT',
        body: { qty, note },
      });
      setItems(res.items);
    } catch {
      setMsg('Could not update that line.');
      load(); // resync so the UI never shows an edit that didn't stick
    } finally {
      setBusy(false);
    }
  }

  async function removeLine(lineId: number) {
    setBusy(true);
    setMsg(null);
    try {
      const res = await apiFetch<OrderItemsResponse>(`/orders/${orderId}/items/${lineId}`, { method: 'DELETE' });
      setItems(res.items);
    } catch {
      setMsg('Could not remove that line.');
      load();
    } finally {
      setBusy(false);
    }
  }

  async function addLine() {
    const typed = newItem.name.trim();
    if (!typed) {
      setMsg('Pick or enter an item.');
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      // A catalog match sends itemId only — the worker snapshots name and unit
      // from `items`. Anything else falls back to a free-text (item_id NULL) line.
      const match = matchCatalogItem(catalog, typed);
      const res = await apiFetch<OrderItemsResponse>(`/orders/${orderId}/items`, {
        method: 'POST',
        body: match
          ? { itemId: match.id, qty: newItem.qty ?? 1, note: newItem.note || null }
          : {
              itemName: typed,
              qty: newItem.qty ?? 1,
              unit: newItem.unit || null,
              note: newItem.note || null,
            },
      });
      setItems(res.items);
      setNewItem({ name: '', qty: 1, unit: '', note: '' });
    } catch (err) {
      // The worker rejects duplicates and dead catalog ids with a usable message.
      const detail = err instanceof Error ? err.message.replace(/^API \d+: /, '') : '';
      let parsed = '';
      try {
        parsed = (JSON.parse(detail) as { error?: string }).error ?? '';
      } catch {
        parsed = '';
      }
      setMsg(parsed || 'Could not add that item.');
    } finally {
      setBusy(false);
    }
  }

  async function sendToVendor() {
    if (!recipient.includes('@')) {
      setMsg('Enter a valid recipient email.');
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const res = await apiFetch<OrderSendResponse>(`/orders/${orderId}/send`, {
        method: 'POST',
        body: { recipientEmail: recipient },
      });
      setMsg(res.message);
      load();
    } catch {
      setMsg('Failed to send order.');
    } finally {
      setBusy(false);
    }
  }

  if (!order) return <div className="p-6 text-zinc-400">Loading…</div>;

  const isVendor = order.channel === 'vendor';
  // 'approved' is legacy (the gate is gone) but such an order must still move.
  const isSendable = order.status === 'submitted' || order.status === 'approved';
  const isEditable = EDITABLE.includes(order.status);

  // A vendor PO suggests only that vendor's items; a shopping list suggests the
  // whole program. See suggestionsFor.
  const pickerOptions = suggestionsFor(order, catalog);
  // Group the flat allocation rows once, so each line can look its own up.
  const splitByLine = new Map<number, OrderLineStation[]>();
  for (const sp of stationSplit) {
    splitByLine.set(sp.order_item_id, [...(splitByLine.get(sp.order_item_id) ?? []), sp]);
  }
  const pickedFromCatalog = !!matchCatalogItem(catalog, newItem.name);
  const breakdown = orderBreakdown(items, requestSplit);

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <Link to="/orders" className="text-xs text-dcvfd-accent">
        ← Back to orders
      </Link>
      <div className="flex items-center justify-between gap-3 mt-2 mb-4">
        <h1 className="flex flex-wrap items-center gap-2 text-xl font-bold text-white">
          <span className="capitalize">
            {isVendor ? (order.vendor_name ?? 'Vendor') : 'Grocery'} order #{order.id}
          </span>
          {/* Which program's list this is — it decides who fulfils it. */}
          <ProgramBadge program={order.program} />
        </h1>
        <span className="text-sm font-semibold text-dcvfd-accent">
          {ORDER_STATUS_LABELS[order.status] ?? order.status}
        </span>
      </div>

      {msg && <div className="mb-4 rounded-lg bg-surface-raised px-3 py-2 text-sm text-zinc-200">{msg}</div>}

      <div className="glass rounded-xl p-4 mb-4">
        {items.length === 0 && <p className="text-sm text-zinc-500">No items left on this order.</p>}
        {items.map((it) =>
          isEditable ? (
            <OrderLineEditor
              key={it.id}
              line={it}
              stations={splitByLine.get(it.id) ?? []}
              busy={busy}
              onSave={saveLine}
              onRemove={removeLine}
            />
          ) : (
            <div
              key={it.id}
              className="flex justify-between gap-3 text-sm text-white py-1 border-b border-border-subtle/50 last:border-0"
            >
              <span className="min-w-0">
                {it.item_name}
                {it.note && <span className="block text-xs text-zinc-500">{it.note}</span>}
                {(splitByLine.get(it.id) ?? []).length > 0 && (
                  <span className="block text-xs text-zinc-400">
                    {(splitByLine.get(it.id) ?? []).map((sp) => `${sp.station_name} ${sp.qty}`).join('  ·  ')}
                  </span>
                )}
              </span>
              <span className="font-mono text-zinc-300 shrink-0">
                {it.qty} {it.unit ?? ''}
              </span>
            </div>
          ),
        )}

        {isEditable && (
          <div className="mt-4 border-t border-border-subtle pt-3">
            <p className="text-xs text-zinc-400 mb-2">Add an item — a substitute, or something else needed.</p>
            <div className="flex gap-2 items-start">
              <input
                list="catalog-items"
                value={newItem.name}
                onChange={(e) => {
                  const name = e.target.value;
                  const match = matchCatalogItem(catalog, name);
                  // Picking from the catalog fills in its unit; the worker is
                  // still the authority on what gets stored.
                  setNewItem((n) => ({ ...n, name, unit: match?.unit ?? n.unit }));
                }}
                placeholder="Start typing an item…"
                // min-w-0: an input's intrinsic ~20ch width otherwise stops it
                // shrinking, and pushes the unit box off a phone screen.
                className="flex-1 min-w-0 min-h-[44px] rounded-lg bg-surface-raised border border-border-subtle px-3 text-sm text-white"
              />
              <NumericInput
                value={newItem.qty}
                onChange={(v) => setNewItem((n) => ({ ...n, qty: v }))}
                min={1}
                aria-label="New item quantity"
              />
              <input
                value={newItem.unit}
                onChange={(e) => setNewItem((n) => ({ ...n, unit: e.target.value }))}
                placeholder="unit"
                disabled={pickedFromCatalog}
                className="w-20 shrink-0 min-h-[44px] rounded-lg bg-surface-raised border border-border-subtle px-2 text-sm text-white disabled:opacity-60"
              />
            </div>
            <datalist id="catalog-items">
              {pickerOptions.map((i) => (
                <option key={i.id} value={i.name} />
              ))}
            </datalist>
            <p className="mt-1 text-xs text-zinc-500">
              {pickedFromCatalog
                ? 'From the catalog — links back to the item record.'
                : newItem.name.trim()
                  ? 'Not in the catalog — will be added as a one-off line.'
                  : isVendor
                    ? `Suggesting ${pickerOptions.length} item${pickerOptions.length === 1 ? '' : 's'} from ${order.vendor_name ?? 'this vendor'}. Anything else can be typed in.`
                    : `Suggesting all ${pickerOptions.length} ${PROGRAM_LABELS[order.program].toLowerCase()} item${pickerOptions.length === 1 ? '' : 's'}. Anything else can be typed in.`}
            </p>
            <div className="flex gap-2 mt-2">
              <input
                value={newItem.note}
                onChange={(e) => setNewItem((n) => ({ ...n, note: e.target.value }))}
                placeholder="Note (optional) — e.g. substitute for milk"
                className="flex-1 min-w-0 min-h-[44px] rounded-lg bg-surface-raised border border-border-subtle px-3 text-sm text-white"
              />
              <button onClick={addLine} disabled={busy} className="btn-primary min-h-[44px] px-4">
                Add
              </button>
            </div>
          </div>
        )}
      </div>

      {/*
        The same order read back the way it came in: each station, then each
        request it sent. Quantities are as requested, so a line bought short
        shows its full ask here and the shortfall on the line above.
      */}
      {breakdown.length > 0 && (
        <div className="glass rounded-xl p-4 mb-4">
          <h2 className="text-sm font-semibold text-white mb-3">By station & request</h2>
          <div className="space-y-4">
            {breakdown.map((st) => (
              <section key={st.stationId}>
                <h3 className="text-sm font-bold text-dcvfd-accent">{st.stationName}</h3>
                {st.requests.map((rq) => (
                  <div key={rq.requestId} className="mt-2 border-l-2 border-border-subtle pl-3">
                    <p className="text-xs text-zinc-400">
                      Request #{rq.requestId} · {rq.submitterName ?? 'Unknown'} · {formatSubmitted(rq.submittedAt)}
                    </p>
                    {rq.lines.map((ln) => (
                      <div key={ln.orderItemId} className="flex justify-between gap-3 py-0.5 text-sm text-white">
                        <span className="min-w-0">{ln.name}</span>
                        <span className="font-mono text-zinc-300 shrink-0">
                          {ln.qty} {ln.unit ?? ''}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </section>
            ))}
          </div>
        </div>
      )}

      {/* Workflow actions — no approval step; orders are hand-delivered. */}
      <div className="flex flex-wrap gap-2">
        {order.status === 'draft' && (
          <button onClick={() => setStatus('submitted')} disabled={busy} className="btn-primary">
            Submit order
          </button>
        )}
        {isSendable && !isVendor && (
          <button onClick={() => setStatus('placed')} disabled={busy} className="btn-primary">
            Mark shopped
          </button>
        )}
        {order.status === 'placed' && (
          <button onClick={() => setStatus('received')} disabled={busy} className="btn-primary">
            Mark delivered
          </button>
        )}
        {(order.status === 'draft' || isSendable) && (
          <button onClick={() => setStatus('cancelled')} disabled={busy} className="btn-ghost">
            Cancel
          </button>
        )}
        {/*
          Opens in its own tab: printing is a side errand, and swapping the page
          out from under someone mid-edit loses their place in the order. `rel`
          goes with `target="_blank"` so the new tab can't reach back via
          window.opener.
        */}
        <Link
          to={`/orders/${order.id}/print`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-ghost"
          aria-label="Print this order (opens in a new tab)"
        >
          Print <span aria-hidden="true">↗</span>
        </Link>
      </div>

      {/* Vendor send screen */}
      {isVendor && isSendable && (
        <div className="glass rounded-xl p-4 mt-5">
          <h2 className="text-sm font-semibold text-white mb-2">Send to vendor</h2>
          <input
            list="email-history"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="vendor@example.com"
            className="w-full rounded-lg bg-surface-raised border border-border-subtle px-3 py-2 text-sm text-white mb-2"
          />
          <datalist id="email-history">
            {emailHistory.map((e) => (
              <option key={e} value={e} />
            ))}
          </datalist>
          <button onClick={sendToVendor} disabled={busy} className="btn-primary w-full">
            {busy ? 'Sending…' : 'Send order & mark placed'}
          </button>

          {/*
            Some vendors are also stores we walk into (BJ's): the order can be
            bought in person like a shopping list instead of emailed. Same
            submitted → placed step as "Mark shopped"; nothing is sent.
          */}
          <div className="my-4 flex items-center gap-3 text-xs text-zinc-500">
            <span className="h-px flex-1 bg-border-subtle" />
            or
            <span className="h-px flex-1 bg-border-subtle" />
          </div>
          <p className="text-xs text-zinc-400 mb-2">
            Buying it in person instead? Print the list, shop it, then mark it shopped. Nothing is emailed to the
            vendor.
          </p>
          <button onClick={() => setStatus('placed')} disabled={busy} className="btn-ghost w-full">
            Mark shopped (no email)
          </button>
        </div>
      )}
      {isVendor && order.recipient_email && (
        <p className="text-xs text-zinc-500 mt-3">Last sent to {order.recipient_email}</p>
      )}
    </div>
  );
}
